/**
 * Firestore document parsers for quiz collections. Defensive defaults so a malformed or
 * older doc never crashes a listener.
 */

import type { DocumentData, Timestamp } from 'firebase/firestore';
import type {
  Quiz,
  QuizAssignment,
  QuizGrade,
  QuizKey,
  QuizQuestion,
  QuizResponse,
  QuizSettings,
  QuizStatus,
  ScenarioSlot,
} from '../types';
import { isScenarioRole } from './scenario';

const QUIZ_STATUSES: QuizStatus[] = ['draft', 'published', 'closed', 'archived'];

const ts = (v: unknown): Timestamp | null => (v && typeof v === 'object' ? (v as Timestamp) : null);
const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);

const nonEmpty = (v: unknown): v is string => typeof v === 'string' && v !== '';

/** Also parses the question snapshots inside scenario step docs. */
export function parseQuestions(raw: unknown): QuizQuestion[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((q) => {
    const question: QuizQuestion = {
      id: str(q?.id),
      type: q?.type ?? 'single',
      prompt: str(q?.prompt),
      choices: Array.isArray(q?.choices)
        ? q.choices.map((c: DocumentData) => ({ id: str(c?.id), text: str(c?.text) }))
        : [],
      required: q?.required !== false,
      imageUrl: typeof q?.imageUrl === 'string' && q.imageUrl ? q.imageUrl : null,
      imagePath: typeof q?.imagePath === 'string' && q.imagePath ? q.imagePath : null,
    };
    // Scenario fields: only present when set, so a normal quiz round-trips unchanged
    if (nonEmpty(q?.slotId)) question.slotId = q.slotId;
    if (nonEmpty(q?.answerKey)) question.answerKey = q.answerKey;
    if (nonEmpty(q?.answerLabel)) question.answerLabel = q.answerLabel;
    return question;
  });
}

/** Slots with an unknown role or no id are dropped (their questions then fail validation). */
function parseSlots(raw: unknown): ScenarioSlot[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((s) => nonEmpty(s?.id) && isScenarioRole(s?.role))
    .map((s) => ({ id: s.id, role: s.role, label: str(s.label) }));
}

function parseSettings(raw: DocumentData | undefined): QuizSettings {
  return {
    passingPercent: typeof raw?.passingPercent === 'number' ? raw.passingPercent : null,
    maxAttempts: typeof raw?.maxAttempts === 'number' ? raw.maxAttempts : null,
    feedbackMode: raw?.feedbackMode === 'none' || raw?.feedbackMode === 'answers' ? raw.feedbackMode : 'score',
    shuffleQuestions: raw?.shuffleQuestions === true,
    shuffleChoices: raw?.shuffleChoices === true,
  };
}

export function parseQuiz(id: string, data: DocumentData): Quiz {
  const status = QUIZ_STATUSES.includes(data.status) ? (data.status as QuizStatus) : 'draft';
  const quiz: Quiz = {
    id,
    title: str(data.title),
    description: str(data.description),
    status,
    questions: parseQuestions(data.questions),
    variables: Array.isArray(data.variables)
      ? data.variables.map((v: DocumentData) => ({ key: str(v?.key), label: str(v?.label) }))
      : [],
    settings: parseSettings(data.settings),
    createdBy: str(data.createdBy),
    createdAt: ts(data.createdAt),
    lastModifiedBy: str(data.lastModifiedBy),
    lastModifiedAt: ts(data.lastModifiedAt),
  };
  if (data.publishedAt) quiz.publishedAt = ts(data.publishedAt);
  if (data.closedAt) quiz.closedAt = ts(data.closedAt);
  if (data.kind === 'scenario') {
    quiz.kind = 'scenario';
    quiz.slots = parseSlots(data.slots);
  }
  return quiz;
}

export function parseKey(data: DocumentData): QuizKey {
  const questions: QuizKey['questions'] = {};
  const raw = (data.questions ?? {}) as Record<string, DocumentData>;
  for (const [qid, e] of Object.entries(raw)) {
    questions[qid] = {
      correctChoiceIds: Array.isArray(e?.correctChoiceIds) ? e.correctChoiceIds.map(String) : [],
      points: typeof e?.points === 'number' ? e.points : 1,
    };
  }
  return { questions };
}

export function parseAssignment(id: string, data: DocumentData): QuizAssignment {
  return {
    id,
    quizId: str(data.quizId),
    quizTitle: str(data.quizTitle),
    assigneeEmail: str(data.assigneeEmail),
    assigneeName: str(data.assigneeName),
    assignedBy: str(data.assignedBy),
    assignedAt: ts(data.assignedAt),
    dueAt: ts(data.dueAt),
    availableAt: ts(data.availableAt),
    variables: data.variables && typeof data.variables === 'object' ? { ...data.variables } : {},
    status: data.status ?? 'assigned',
    attemptsUsed: typeof data.attemptsUsed === 'number' ? data.attemptsUsed : 0,
    activeResponseId: typeof data.activeResponseId === 'string' ? data.activeResponseId : null,
    lastSubmittedAt: ts(data.lastSubmittedAt),
    visibleResult: data.visibleResult
      ? {
          percent: Number(data.visibleResult.percent) || 0,
          passed: typeof data.visibleResult.passed === 'boolean' ? data.visibleResult.passed : null,
        }
      : null,
    locationId: nonEmpty(data.locationId) ? data.locationId : null,
    storeResult:
      data.storeResult && typeof data.storeResult === 'object'
        ? {
            percent: typeof data.storeResult.percent === 'number' ? data.storeResult.percent : null,
            passed: typeof data.storeResult.passed === 'boolean' ? data.storeResult.passed : null,
          }
        : null,
  };
}

export function parseResponse(id: string, data: DocumentData): QuizResponse {
  return {
    id,
    assignmentId: str(data.assignmentId),
    quizId: str(data.quizId),
    assigneeEmail: str(data.assigneeEmail),
    attempt: typeof data.attempt === 'number' ? data.attempt : 1,
    status: data.status === 'submitted' ? 'submitted' : 'draft',
    answers: data.answers && typeof data.answers === 'object' ? { ...data.answers } : {},
    startedAt: ts(data.startedAt),
    updatedAt: ts(data.updatedAt),
    submittedAt: ts(data.submittedAt),
    late: data.late === true,
    feedback: data.feedback && typeof data.feedback === 'object' ? { ...data.feedback } : null,
    feedbackRevealed: data.feedbackRevealed === true,
  };
}

export function parseGrade(id: string, data: DocumentData): QuizGrade {
  return {
    id,
    quizId: str(data.quizId),
    assignmentId: str(data.assignmentId),
    assigneeEmail: str(data.assigneeEmail),
    attempt: typeof data.attempt === 'number' ? data.attempt : 1,
    score: typeof data.score === 'number' ? data.score : 0,
    maxScore: typeof data.maxScore === 'number' ? data.maxScore : 0,
    percent: typeof data.percent === 'number' ? data.percent : null,
    passed: typeof data.passed === 'boolean' ? data.passed : null,
    questionResults: data.questionResults && typeof data.questionResults === 'object' ? { ...data.questionResults } : {},
    gradedAt: ts(data.gradedAt),
    late: data.late === true,
  };
}
